/**
 * Utility functions for the UnifiedCustomerSelector component
 */

import { emailIsValid } from "../../../../../../utils";

import type {
  CustomerSelection,
  CustomerSelectionItem,
  CustomerSelectionResult,
} from "./types";
import type { CustomerListItem } from "../../../../../SupplierNetwork/types";

// Customer type constants
export const CUSTOMER_TYPES = {
  INTERNAL: "internal_customer",
  EXTERNAL: "external_customer",
  EMAIL: "email",
} as const;

// Email separation patterns for paste handling
const PASTE_SEPARATORS_REGEX = /[,;\s\n\r]/;

/**
 * Validates if a string is a valid email address
 */
export const isValidEmail = (email: string): boolean =>
  emailIsValid(email.trim());

/**
 * Formats a customer's display name
 */
const formatCustomerDisplayName = (customer: CustomerSelectionItem): string => {
  if (customer.type === CUSTOMER_TYPES.EMAIL) {
    return customer.email;
  }

  const firstName = customer.first_name ?? "";
  const lastName = customer.last_name ?? "";
  const companyName = customer.company_name ?? "";

  if (firstName && lastName) {
    return companyName
      ? `${firstName} ${lastName} (${companyName})`
      : `${firstName} ${lastName}`;
  }

  if (companyName) {
    return `${customer.email} (${companyName})`;
  }

  return customer.email;
};

/**
 * Transforms CustomerListItem (from useSupplierCustomers) into CustomerSelectionItem
 */
export const transformCustomerListItem = (
  customer: CustomerListItem,
): CustomerSelectionItem => {
  if (customer.type === "INTERNAL") {
    if (customer.role_id === null) {
      throw new Error(`Internal customer ${customer.id} has no role id`);
    }

    return {
      type: CUSTOMER_TYPES.INTERNAL,
      id: customer.id,
      role_id: customer.role_id,
      email: customer.email,
      first_name: customer.first_name,
      last_name: customer.last_name,
      company_name: customer.company_name,
      isDisabled: customer.is_disabled,
    };
  }

  return {
    type: CUSTOMER_TYPES.EXTERNAL,
    id: customer.id,
    email: customer.email,
    first_name: customer.first_name,
    last_name: customer.last_name,
    company_name: customer.company_name,
  };
};

/**
 * Creates an email customer item from a string
 */
export const createEmailCustomer = (email: string): CustomerSelectionItem => ({
  type: CUSTOMER_TYPES.EMAIL,
  email: email.trim().toLowerCase(),
});

/**
 * Processes user selections and separates them into the appropriate categories
 */
export const processCustomerSelections = (
  selections: CustomerSelection[],
): CustomerSelectionResult => {
  const customerRoleIds: number[] = [];
  const externalCustomerIds: number[] = [];
  const customerInviteEmails: string[] = [];

  selections.forEach((selection) => {
    if (typeof selection === "string") {
      // User typed an email address
      if (isValidEmail(selection)) {
        customerInviteEmails.push(selection.trim().toLowerCase());
      }
    } else {
      // User selected an existing customer
      switch (selection.type) {
        case CUSTOMER_TYPES.INTERNAL:
          customerRoleIds.push(selection.role_id);
          break;
        case CUSTOMER_TYPES.EXTERNAL:
          externalCustomerIds.push(selection.id);
          break;
        case CUSTOMER_TYPES.EMAIL:
          if (isValidEmail(selection.email)) {
            customerInviteEmails.push(selection.email.trim().toLowerCase());
          }
          break;
        default:
          // Unknown selection type, ignore
          break;
      }
    }
  });

  // Remove duplicates
  return {
    customerRoleIds: Array.from(new Set(customerRoleIds)),
    externalCustomerIds: Array.from(new Set(externalCustomerIds)),
    customerInviteEmails: Array.from(new Set(customerInviteEmails)),
  };
};

/**
 * Sorts customers for display in autocomplete
 * Internal customers first, then external customers, then emails
 */
export const sortCustomerOptions = (
  customers: CustomerSelectionItem[],
): CustomerSelectionItem[] =>
  [...customers].sort((a, b) => {
    // Sort by type: internal_customer < external_customer < email
    const typeOrder = {
      [CUSTOMER_TYPES.INTERNAL]: 0,
      [CUSTOMER_TYPES.EXTERNAL]: 1,
      [CUSTOMER_TYPES.EMAIL]: 2,
    };
    const aOrder = typeOrder[a.type];
    const bOrder = typeOrder[b.type];

    if (aOrder !== bOrder) {
      return aOrder - bOrder;
    }

    // Within same type, sort alphabetically by display name
    const aName = formatCustomerDisplayName(a).toLowerCase();
    const bName = formatCustomerDisplayName(b).toLowerCase();
    return aName.localeCompare(bName);
  });

/**
 * Gets a unique key for a customer item (for React keys)
 */
export const getCustomerKey = (customer: CustomerSelectionItem): string => {
  switch (customer.type) {
    case CUSTOMER_TYPES.INTERNAL:
      return `internal-${customer.role_id}`;
    case CUSTOMER_TYPES.EXTERNAL:
      return `external-${customer.id}`;
    case CUSTOMER_TYPES.EMAIL:
      return `email-${customer.email}`;
    default:
      // This should never happen as we handle all union types above
      throw new Error(`Unknown customer type: ${(customer as any).type}`);
  }
};

/**
 * Checks if two customers are the same
 */
export const areCustomersEqual = (
  a: CustomerSelectionItem,
  b: CustomerSelectionItem,
): boolean => {
  if (a.type !== b.type) {
    return false;
  }

  switch (a.type) {
    case CUSTOMER_TYPES.INTERNAL:
      return a.role_id === (b as typeof a).role_id;
    case CUSTOMER_TYPES.EXTERNAL:
      return a.id === (b as typeof a).id;
    case CUSTOMER_TYPES.EMAIL:
      return a.email.toLowerCase() === (b as typeof a).email.toLowerCase();
    default:
      return false;
  }
};

/**
 * Interface for paste handling props
 */
interface PasteHandlerProps {
  currentSelections: CustomerSelectionItem[];
  availableCustomers: CustomerSelectionItem[];
  onSelectionChange: (selections: CustomerSelection[]) => void;
  onInputChange: (value: string) => void;
  onErrorChange?: (error: string) => void;
}

/**
 * Handles pasting multiple emails separated by commas, semicolons, spaces, or newlines
 */
export const handleCustomerPaste = (
  event: React.ClipboardEvent,
  props: PasteHandlerProps,
): void => {
  const { clipboardData } = event;
  if (!clipboardData) {
    return;
  }

  const pastedText = clipboardData.getData("text");
  if (!pastedText.trim()) return;

  const trimmedPaste = pastedText.trim();

  // Check if paste contains multiple items (has separators)
  const hasSeparators = PASTE_SEPARATORS_REGEX.test(trimmedPaste);

  if (hasSeparators) {
    // Prevent default paste behavior
    event.preventDefault();

    // Split the pasted text by various separators
    const emailParts = trimmedPaste
      .split(/[,;\s\n\r]+/)
      .map((part) => part.trim())
      .filter((part) => part.length > 0);

    // Process each email part
    const newSelections: CustomerSelection[] = [...props.currentSelections];
    const invalidEmails: string[] = [];

    emailParts.forEach((emailPart) => {
      // Check if it's a valid email
      if (isValidEmail(emailPart)) {
        // Check if email already exists in current selections
        const emailExists = newSelections.some((selection) => {
          if (typeof selection === "string") {
            return selection.toLowerCase() === emailPart.toLowerCase();
          }
          return selection.email.toLowerCase() === emailPart.toLowerCase();
        });

        if (!emailExists) {
          // Try to find existing customer with this email
          const existingCustomer = props.availableCustomers.find(
            (customer) =>
              customer.email.toLowerCase() === emailPart.toLowerCase(),
          );

          if (existingCustomer) {
            // Add as existing customer
            newSelections.push(existingCustomer);
          } else {
            // Add as email invite (normalized to lowercase)
            newSelections.push(emailPart.toLowerCase());
          }
        }
      } else {
        // Track invalid emails
        invalidEmails.push(emailPart);
      }
    });

    // Update selections
    props.onSelectionChange(newSelections);

    // If there are invalid emails, keep them in the input field for user to fix
    if (invalidEmails.length > 0) {
      props.onInputChange(invalidEmails.join(", "));
      props.onErrorChange?.(
        `Invalid email${invalidEmails.length > 1 ? "s" : ""}: ${invalidEmails.join(", ")}`,
      );
    } else {
      // Clear the input field only if all emails were valid
      props.onInputChange("");
      props.onErrorChange?.("");
    }
  }
};
